import { suppliesShippingConditionStepSchema } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  SuppliesShippingConditionKind,
  SuppliesShippingListOperator,
  SuppliesShippingNumberOperator,
} from "@redotech/redo-flows-core-schema-common/common";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { FrontendStepType, StepTypeDetailsProps } from "..";
import {
  suppliesShippingListOperatorLabel,
  suppliesShippingOperatorLabel,
} from "../../nodes/supplies-shipping-condition-summary";
import { SuppliesFamilyTagSelect } from "./supplies-family-tag-select";
import {
  SuppliesInspectorShell,
  SuppliesMoneyField,
} from "./supplies-inspector-inputs";
import { SuppliesMerchantSelect } from "./supplies-merchant-select";
import { SuppliesProductSelect } from "./supplies-product-select";
import { SuppliesProvinceSelect } from "./supplies-province-select";
import {
  SuppliesCondition,
  SuppliesConditionStep,
  toSuppliesCondition,
} from "./supplies-shipping-step-values";

type ConditionStep = SuppliesConditionStep;

function kindLabel(kind: SuppliesShippingConditionKind): string {
  switch (kind) {
    case SuppliesShippingConditionKind.CART_VALUE:
      return t("Cart value");
    case SuppliesShippingConditionKind.FIRST_TIME_CUSTOMER:
      return t("First supplies order");
    case SuppliesShippingConditionKind.AUTOMATED_REORDER:
      return t("Automated reorder");
    case SuppliesShippingConditionKind.MERCHANT:
      return t("Merchant");
    case SuppliesShippingConditionKind.CART_ITEMS:
      return t("Cart items");
    case SuppliesShippingConditionKind.PRODUCT_FAMILY_TAGS:
      return t("Product family tags");
    case SuppliesShippingConditionKind.DESTINATION_PROVINCE:
      return t("Destination state");
  }
}

const KIND_OPTIONS = Object.values(SuppliesShippingConditionKind);
const OPERATOR_OPTIONS = Object.values(SuppliesShippingNumberOperator);

// RedoList renders by `type`; an option missing it hits the list's assertNever.
function kindOption(kind: SuppliesShippingConditionKind) {
  return {
    id: kind,
    type: "text" as const,
    text: kindLabel(kind),
    value: kind,
  };
}

function operatorOption(operator: SuppliesShippingNumberOperator) {
  return {
    id: operator,
    type: "text" as const,
    text: suppliesShippingOperatorLabel(operator),
    value: operator,
  };
}

type Condition = SuppliesCondition;
type ListCondition = Extract<
  Condition,
  { operator: SuppliesShippingListOperator }
>;

const ANY_OF = SuppliesShippingListOperator.ANY_OF;

const LIST_KINDS = new Set<SuppliesShippingConditionKind>([
  SuppliesShippingConditionKind.MERCHANT,
  SuppliesShippingConditionKind.CART_ITEMS,
  SuppliesShippingConditionKind.PRODUCT_FAMILY_TAGS,
  SuppliesShippingConditionKind.DESTINATION_PROVINCE,
]);

function isListCondition(
  condition: Condition | undefined,
): condition is ListCondition {
  return !!condition && LIST_KINDS.has(condition.kind);
}

function listOperatorOption(operator: SuppliesShippingListOperator) {
  return {
    id: operator,
    type: "text" as const,
    text: suppliesShippingListOperatorLabel(operator),
    value: operator,
  };
}

const LIST_OPERATOR_OPTIONS = Object.values(SuppliesShippingListOperator);

function emptyConditionOfKind(kind: SuppliesShippingConditionKind): Condition {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (kind) {
    case SuppliesShippingConditionKind.CART_VALUE:
      return {
        kind,
        operator: SuppliesShippingNumberOperator.GREATER_THAN_OR_EQUAL,
        amountMinorUnits: 0,
      };
    case SuppliesShippingConditionKind.MERCHANT:
      return { kind, operator: ANY_OF, accountIds: [] };
    case SuppliesShippingConditionKind.CART_ITEMS:
      return { kind, operator: ANY_OF, productIds: [] };
    case SuppliesShippingConditionKind.PRODUCT_FAMILY_TAGS:
      return { kind, operator: ANY_OF, tags: [] };
    case SuppliesShippingConditionKind.DESTINATION_PROVINCE:
      return { kind, operator: ANY_OF, provinces: [] };
    default:
      return { kind };
  }
}

const SuppliesShippingConditionInspector = memo(
  function SuppliesShippingConditionInspector(
    props: StepTypeDetailsProps<ConditionStep>,
  ) {
    const { t } = useTranslation();
    const { step, setStep, onClose, readonly } = props;
    const condition = toSuppliesCondition(step.condition);

    // Switching kind replaces the whole condition: the operands differ, and a
    // half-migrated object would fail the discriminated union on save.
    const setKind = (kind: SuppliesShippingConditionKind) => {
      if (kind === condition?.kind) {
        return;
      }
      setStep({ ...step, condition: emptyConditionOfKind(kind) });
    };

    return (
      <SuppliesInspectorShell
        onClose={onClose}
        subtitle={t("Carts matching this condition follow the Yes branch.")}
        title={t("Condition")}
      >
        <Flex dir="column" gap="md">
          <Text fontSize="xxs" textColor="secondary">
            {t("Condition")}
          </Text>
          <RedoSingleSelectDropdownInput
            disabled={readonly}
            options={KIND_OPTIONS.map(kindOption)}
            optionSelected={({ item }) => setKind(item.value)}
            selectedItem={condition ? kindOption(condition.kind) : undefined}
          />
        </Flex>

        {condition?.kind === SuppliesShippingConditionKind.CART_VALUE && (
          <>
            <Flex dir="column" gap="md">
              <Text fontSize="xxs" textColor="secondary">
                {t("Comparison")}
              </Text>
              <RedoSingleSelectDropdownInput
                disabled={readonly}
                options={OPERATOR_OPTIONS.map(operatorOption)}
                optionSelected={({ item }) =>
                  setStep({
                    ...step,
                    condition: { ...condition, operator: item.value },
                  })
                }
                selectedItem={operatorOption(condition.operator)}
              />
            </Flex>
            {condition.operator === SuppliesShippingNumberOperator.BETWEEN ? (
              <>
                <SuppliesMoneyField
                  description={t(
                    "Inclusive: a cart exactly at this amount matches.",
                  )}
                  disabled={readonly}
                  label={t("From")}
                  minorUnits={condition.amountMinorUnits}
                  onCommit={(amountMinorUnits) =>
                    setStep({
                      ...step,
                      condition: { ...condition, amountMinorUnits },
                    })
                  }
                />
                <SuppliesMoneyField
                  description={t(
                    "Inclusive, and must be at or above the lower bound.",
                  )}
                  disabled={readonly}
                  label={t("To")}
                  minorUnits={condition.maxAmountMinorUnits ?? undefined}
                  onCommit={(maxAmountMinorUnits) =>
                    setStep({
                      ...step,
                      condition: { ...condition, maxAmountMinorUnits },
                    })
                  }
                />
              </>
            ) : (
              <SuppliesMoneyField
                description={t(
                  "Compared against the cart total before tax, after any discount.",
                )}
                disabled={readonly}
                label={t("Amount")}
                minorUnits={condition.amountMinorUnits}
                onCommit={(amountMinorUnits) =>
                  setStep({
                    ...step,
                    condition: { ...condition, amountMinorUnits },
                  })
                }
              />
            )}
          </>
        )}

        {condition?.kind ===
          SuppliesShippingConditionKind.FIRST_TIME_CUSTOMER && (
          <Text fontSize="xs" textColor="secondary">
            {t(
              "Matches when this team has never placed a supplies order before.",
            )}
          </Text>
        )}

        {condition?.kind ===
          SuppliesShippingConditionKind.AUTOMATED_REORDER && (
          <Text fontSize="xs" textColor="secondary">
            {t(
              "Matches replenishment and subscription orders placed automatically, not carts a merchant checked out themselves.",
            )}
          </Text>
        )}

        {isListCondition(condition) && (
          <Flex dir="column" gap="md">
            <Text fontSize="xxs" textColor="secondary">
              {t("Match")}
            </Text>
            <RedoSingleSelectDropdownInput
              disabled={readonly}
              options={LIST_OPERATOR_OPTIONS.map(listOperatorOption)}
              optionSelected={({ item }) =>
                setStep({
                  ...step,
                  condition: { ...condition, operator: item.value },
                })
              }
              selectedItem={listOperatorOption(condition.operator)}
              size="xs"
            />
          </Flex>
        )}

        {condition?.kind ===
          SuppliesShippingConditionKind.DESTINATION_PROVINCE && (
          <SuppliesProvinceSelect
            disabled={readonly}
            provinces={condition.provinces}
            setProvinces={(provinces) =>
              setStep({ ...step, condition: { ...condition, provinces } })
            }
          />
        )}

        {condition?.kind ===
          SuppliesShippingConditionKind.PRODUCT_FAMILY_TAGS && (
          <SuppliesFamilyTagSelect
            disabled={readonly}
            setTags={(tags) =>
              setStep({ ...step, condition: { ...condition, tags } })
            }
            tags={condition.tags}
          />
        )}

        {condition?.kind === SuppliesShippingConditionKind.CART_ITEMS && (
          <SuppliesProductSelect
            disabled={readonly}
            productIds={condition.productIds}
            setProductIds={(productIds) =>
              setStep({ ...step, condition: { ...condition, productIds } })
            }
          />
        )}

        {condition?.kind === SuppliesShippingConditionKind.MERCHANT && (
          <SuppliesMerchantSelect
            accountIds={condition.accountIds}
            disabled={readonly}
            setAccountIds={(accountIds) =>
              setStep({ ...step, condition: { ...condition, accountIds } })
            }
          />
        )}
      </SuppliesInspectorShell>
    );
  },
);

/** BETWEEN needs an upper bound at or above the lower one; zod cannot say so. */
function betweenBoundsValid(step: ConditionStep): boolean {
  const condition = toSuppliesCondition(step.condition);
  if (
    condition?.kind !== SuppliesShippingConditionKind.CART_VALUE ||
    condition.operator !== SuppliesShippingNumberOperator.BETWEEN
  ) {
    return true;
  }
  const max = condition.maxAmountMinorUnits;
  return max != null && max >= condition.amountMinorUnits;
}

export const suppliesShippingConditionStepType: FrontendStepType<ConditionStep> =
  {
    type: "custom",
    valid: (step) =>
      suppliesShippingConditionStepSchema.safeParse(step).success &&
      betweenBoundsValid(step),
    customInspector: SuppliesShippingConditionInspector,
  };
